/**
 * Box 6, Budget pacing (DEC5 spec §4): this month (Bangkok) against the
 * approved plan, in one currency, whatever the page's filters. No plan is a
 * sentence and a link to Settings, never 0%. The tone comes from the status
 * pacingOf decided, never from the rounded % shown (m4).
 *
 * @module components/looker/app/BudgetPacingBox
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/config/brand';
import { shortDate } from '@/utils/shortDate';
import { platformLabel } from '@/utils/platformLabel';
import { shownProjection, type BudgetPacingChannel, type BudgetPacingView } from '@/services/looker/budgetPacingData';
import type { Pacing, PacingStatus } from '@/services/targets/targetRules';

type TFn = (key: string, options?: Record<string, unknown>) => string;

/** Literal keys, so the locale-integrity guard sees every one. */
const STATUS_TEXT: Record<PacingStatus, (t: TFn) => string> = {
  under: (t) => t('looker.app.budgetPacing.status.under'),
  onTrack: (t) => t('looker.app.budgetPacing.status.onTrack'),
  over: (t) => t('looker.app.budgetPacing.status.over'),
  tooEarly: (t) => t('looker.app.budgetPacing.status.tooEarly'),
};

const TONE: Record<PacingStatus, string> = {
  under: 'text-warning-700',
  onTrack: 'text-success-700',
  over: 'text-error-700',
  tooEarly: 'text-neutral-700',
};

/** "ตุลาคม 2026" / "October 2026": Gregorian years in Thai too, as the spec writes them. */
const monthName = (month: string, language: string): string =>
  new Intl.DateTimeFormat(language.startsWith('th') ? 'th-TH-u-ca-gregory' : 'en-GB', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${month}T00:00:00Z`));

/** "ตามแผน 98%"; too early shows no %. */
const statusText = (p: Pacing, t: TFn): string =>
  p.pct === null ? STATUS_TEXT[p.status](t) : `${STATUS_TEXT[p.status](t)} ${p.pct}%`;

const ChannelLine: React.FC<{ c: BudgetPacingChannel; money: (n: number) => string }> = ({ c, money }) => {
  const { t } = useTranslation();
  return (
    <li className="flex justify-between gap-3 text-sm">
      <span className="text-neutral-900">{platformLabel(c.platform)}</span>
      {c.pacing ? (
        <span className={`font-medium tabular-nums ${TONE[c.pacing.status]}`}>{statusText(c.pacing, t)}</span>
      ) : (
        <span className="tabular-nums text-neutral-700">{t('looker.app.budgetPacing.noPlanForChannel', { spend: money(c.spend) })}</span>
      )}
    </li>
  );
};

const NoPlan: React.FC<{ view: Extract<BudgetPacingView, { kind: 'noPlan' }> }> = ({ view }) => {
  const { t } = useTranslation();
  const text = view.reason === 'pickCurrency'
    ? t('looker.app.budgetPacing.pickCurrency')
    : view.currency
      // Named whenever known: a plan may exist in another currency (I2).
      ? t('looker.app.budgetPacing.noPlanFor', { currency: view.currency })
      : t('looker.app.budgetPacing.noPlan');
  return (
    <div className="py-4 text-center text-sm text-neutral-700">
      <p>{text}</p>
      {view.reason === 'none' && (
        <Link to="/settings/targets" className="mt-2 inline-block font-medium text-neutral-900 underline">
          {t('looker.app.budgetPacing.setPlan')}
        </Link>
      )}
    </div>
  );
};

export const BudgetPacingBox: React.FC<{ view: BudgetPacingView }> = ({ view }) => {
  const { t, i18n } = useTranslation();
  const header = <p className="mb-2 text-xs text-neutral-700">{t('looker.app.budgetPacing.header', { month: monthName(view.month, i18n.language) })}</p>;
  if (view.kind === 'noPlan') return <div>{header}<NoPlan view={view} /></div>;
  const money = (n: number): string => formatCurrency(n, true, view.currency);
  const shown = shownProjection(view.pacing);
  const projected = shown === null ? '—' : money(shown);
  return (
    <div>
      {header}
      <p className={`text-lg font-semibold tabular-nums ${TONE[view.pacing.status]}`}>{statusText(view.pacing, t)}</p>
      <p className="mt-1 text-sm tabular-nums text-neutral-900">
        {t('looker.app.budgetPacing.spendOfPlan', { spend: money(view.spend), plan: money(view.plan), projected })}
      </p>
      <p className="mt-1 text-xs text-neutral-700">
        {view.dataThrough
          ? t('looker.app.budgetPacing.dataThrough', { date: shortDate(view.dataThrough, t), days: view.days, daysInMonth: view.daysInMonth })
          : t('looker.app.budgetPacing.noDataYet')}
      </p>
      {view.planDerived && <p className="mt-1 text-xs text-neutral-700">{t('looker.app.budgetPacing.derived')}</p>}
      {view.mismatch && (
        <p className="mt-1 text-xs text-warning-700">
          {t('looker.app.budgetPacing.mismatch', { channelSum: money(view.mismatch.channelSum), total: money(view.mismatch.total) })}
        </p>
      )}
      {view.unknownCurrencyExcluded && <p className="mt-1 text-xs text-neutral-700">{t('looker.app.budgetPacing.unknownCurrency')}</p>}
      {view.channels.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-neutral-100 pt-2">
          {view.channels.map((c) => <ChannelLine key={c.platform} c={c} money={money} />)}
        </ul>
      )}
    </div>
  );
};
